import Surface from '../../../../components/layout/Surface';
import { UAParser } from 'ua-parser-js';
import type { GetUserSessionsRequest } from '@gdladder/shared/features';
import { XIcon } from '../../../../components/shared/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import APIClient from '../../../../api/APIClient';
import { toast } from 'react-toastify';
import type { Session } from '@gdladder/shared/entities';
import renderToastError from '../../../../utils/renderToastError';
import type { AxiosError } from 'axios';
import { useNavigate } from 'react-router';
import { routes } from '../../../../routes/route-definitions';

interface Props {
    session: GetUserSessionsRequest['data'][number];
}

export function SessionCard({ session }: Props) {
    const ua = UAParser(session.userAgent);

    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const revokeSessionMutation = useMutation({
        mutationFn: () => APIClient.delete(`/sessions/${session.id}`),
        onMutate: () => toast.loading('Revoking session...'),
        onSuccess: (_data, _variables, toastId) => {
            if (session.current) {
                window.location.href = '/';
                return;
            }

            toast.update(toastId, { render: 'Session revoked', type: 'success', isLoading: false, autoClose: 5000 });
            queryClient.setQueryData(['sessions'], (prev: Session[]) => prev.filter((s) => s.id !== session.id));
        },
        onError: (error: AxiosError, _variables, toastId) => {
            if ((error.response?.data as { message?: string | undefined })?.message === 'TOTP_REQUIRED') {
                void navigate(routes.auth.totp.href());
                return toast.update(toastId!, {
                    render: 'Two-factor authentication required!',
                    type: 'warning',
                    isLoading: false,
                    autoClose: 5000,
                });
            }

            renderToastError.error(toastId!, error);
        },
    });

    return (
        <li>
            <Surface variant='700'>
                <div className='flex items-center justify-between'>
                    <p>
                        <b>
                            {ua.os.name ?? 'Other'} - {ua.browser.name ?? 'unknown (' + session.ipAddress + ')'}
                        </b>
                    </p>
                    <button onClick={() => revokeSessionMutation.mutate()} disabled={revokeSessionMutation.isPending}>
                        <XIcon />
                    </button>
                </div>
                {session.current && <p className='text-sm text-green-500'>Current session</p>}
                <p className='text-sm text-theme-400'>
                    Started {new Date(session.createdAt).toLocaleString()} - Expires{' '}
                    {new Date(session.expiresAt).toLocaleString()}
                </p>
            </Surface>
        </li>
    );
}
